import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil, filter, tap } from 'rxjs/operators';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as fromPrefs from '../../store';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';

@Component({
    selector: 'topo-register-email',
    templateUrl: './register-email.component.html',
    styleUrls: ['./register-email.component.scss'],
    standalone: false
})
export class RegisterEmailComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isActive: boolean;
  emailRegex = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
  orgCode: string;
  enterCode$: Observable<boolean>;
  err$: Observable<any>;
  destroy$: Subject<void> = new Subject();
  emailField: string;
  @Input() isDesktop: boolean;
  error: string | null = null;
  isLoading = this.store.selectSignal(fromPrefs.selectIsLoading);

  constructor(
    private fb: FormBuilder,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.enterCode$ = this.store.select(fromPrefs.selectShowEnterCode);
    this.err$ = this.store.select(fromPrefs.selectError);

    this.form = this.fb.group({
      email: [],
      code: []
    });

    this.store
      .select(fromPrefs.selectShowRegisterEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.isActive = value;
        this.error = null;

        if (this.isActive) {
          this.form.patchValue({ email: null });
          this.form.patchValue({ code: null });
        }
      });

    this.store
      .select(fromPrefs.selectEmailField)
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => (this.emailField = value));

    this.store
      .select(fromUserProfile.selectOrganization)
      .pipe(takeUntil(this.destroy$))
      .subscribe(org => (this.orgCode = org?.orgCode));

    this.err$
      .pipe(
        filter(() => this.isActive),
        takeUntil(this.destroy$)
      )
      .subscribe(err => {
        this.error = !!err
          ? err?.message?.indexOf('NotAuthorizedException') > 0
            ? 'NOTIFICATION-PREFERENCES.ERROR-INVALID-CODE'
            : 'ERRORS.GENERAL'
          : null;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  requestCodeClick(): void {
    this.error = null;

    if (!!this.form.value.email && this.testIsValidEmail(this.form.value.email)) {
      this.store
        .select(fromAuth.selectUserContext)
        .pipe(
          take(1),
          tap(userContext => {
            this.store.dispatch(
              fromPrefs.sendOneTimeAuthEmail({
                orgCode: this.orgCode,
                email: this.form.value.email,
                cultureCode: userContext.cultureCode,
                recaptchaToken: null
              })
            );
          })
        )
        .subscribe();
    } else {
      this.showInvalidEmail();
    }
  }

  testIsValidEmail(email: string): boolean {
    if (!email) {
      return false;
    }

    return this.emailRegex.test(email);
  }

  showInvalidEmail(): void {
    this.error = 'NOTIFICATION-PREFERENCES.ERROR-ENTER-VALID-EMAIL';
  }

  cancelEmail(): void {
    this.store.dispatch(fromPrefs.cancelEnterCode());
    this.store.dispatch(fromPrefs.cancelChangeEmailField());
  }

  verifyCode(): void {
    if (!this.form.value.code) {
      return;
    }

    this.error = null;

    this.store.dispatch(
      fromPrefs.processAuthCodeForEmail({
        orgCode: this.orgCode,
        code: this.form.value.code,
        userKey: this.form.value.email,
        fieldName: this.emailField
      })
    );
  }
}
